import { useState } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

export default function Booking() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const ENQUIRY_ENDPOINT =
    "https://app.abremponghostel.com/api/public/abrempong-hostel-666865/enquiry";

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg(null);
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data: Record<string, string> = {};
    formData.forEach((value, key) => {
      data[key] = value.toString();
    });
    try {
      const res = await fetch(ENQUIRY_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        setSubmitted(true);
        form.reset();
        setCharCount(0);
      } else {
        const body = await res.json().catch(() => ({}));
        setErrorMsg(body.error || "Could not send. Please try again or call us.");
      }
    } catch {
      setErrorMsg("Network error — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappUrl =
    "https://wa.me/233256112666?text=Hello!%20I%20have%20an%20enquiry%20about%20rooms%20at%20Abrempong%20Hostel%2C%20Madina.%20Could%20you%20share%20more%20details%3F";

  const inputClasses =
    "w-full h-12 px-5 bg-dark-900 rounded-2xl text-sm text-white placeholder-white/25 ring-1 ring-white/[0.06] focus:ring-gold/60 focus:outline-none transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] font-body";

  return (
    <section
      id="booking"
      ref={sectionRef}
      className="w-full bg-dark-850 py-16 md:py-24 lg:py-32 relative overflow-hidden"
    >
      {/* Subtle radial glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold/[0.02] rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-12"
        >
          <span className="inline-block rounded-full px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-medium font-body text-gold ring-1 ring-gold/20 mb-6">
            ENQUIRE
          </span>
          <h2 className="text-white text-3xl md:text-5xl font-bold font-display tracking-tight">
            Have a Question?
          </h2>
          <p className="text-white/50 text-base mt-4 font-body">
            Send us an enquiry and we&apos;ll respond within 24 hours
          </p>
        </motion.div>

        {/* Two-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* LEFT — Booking Form */}
          <motion.div
            initial={{ opacity: 0, x: -60, filter: "blur(6px)" }}
            animate={isInView ? { opacity: 1, x: 0, filter: "blur(0px)" } : {}}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.32, 0.72, 0, 1] }}
            className="rounded-[1.75rem] bg-white/[0.03] p-1.5 ring-1 ring-white/[0.06]"
          >
            <div className="bg-dark-700 rounded-[calc(1.75rem-0.375rem)] p-8 md:p-10 shadow-[inset_0_1px_1px_rgba(255,255,255,0.06)]">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gold/10 mx-auto mb-6">
                  <i className="ri-check-double-line text-gold text-4xl" />
                </div>
                <h3 className="text-white text-2xl font-bold mb-3 font-display">
                  Enquiry Received
                </h3>
                <p className="text-white/50 text-sm font-body mb-8 max-w-sm mx-auto">
                  Thank you! We&apos;ll get back to you within 24 hours with
                  answers to your questions.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="px-8 py-3 border border-gold text-gold text-xs uppercase tracking-widest rounded-full hover:bg-gold hover:text-black transition-all duration-300 cursor-pointer font-body font-semibold"
                >
                  Send Another Enquiry
                </button>
              </div>
            ) : (
              <form
                id="abrempong-enquiry-form"
                onSubmit={handleSubmit}
                className="space-y-5"
                noValidate
              >
                <h3 className="text-white text-xl font-bold font-display mb-2">
                  Make an Enquiry
                </h3>
                <p className="text-white/40 text-sm font-body mb-4">
                  Fill in your details and we&apos;ll get back to you within 24
                  hours.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-white/70 text-xs uppercase tracking-wider mb-2 font-body font-medium">
                      Full Name <span className="text-gold">*</span>
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder="e.g. Kwame Asante"
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-xs uppercase tracking-wider mb-2 font-body font-medium">
                      Phone Number <span className="text-gold">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="+233 XX XXX XXXX"
                      className={inputClasses}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/70 text-xs uppercase tracking-wider mb-2 font-body font-medium">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="your@email.com"
                    className={inputClasses}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-white/70 text-xs uppercase tracking-wider mb-2 font-body font-medium">
                      Preferred Move-in Date
                    </label>
                    <input
                      type="date"
                      name="preferred_move_in"
                      className={`${inputClasses} [color-scheme:dark]`}
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-xs uppercase tracking-wider mb-2 font-body font-medium">
                      Room of Interest
                    </label>
                    <select
                      name="room_of_interest"
                      defaultValue=""
                      className={`${inputClasses} cursor-pointer`}
                    >
                      <option value="">Select room type</option>
                      <option value="1-in-a-Room">
                        1-in-a-Room — GHC 15,000/sem
                      </option>
                      <option value="2-in-a-Room">
                        2-in-a-Room — GHC 8,000/sem
                      </option>
                      <option value="3-in-a-Room">
                        3-in-a-Room — GHC 6,500/sem
                      </option>
                      <option value="4-in-a-Room">
                        4-in-a-Room — GHC 5,500/sem
                      </option>
                      <option value="Not sure yet">Not sure yet</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-white/70 text-xs uppercase tracking-wider mb-2 font-body font-medium">
                    Your Enquiry <span className="text-gold">*</span>
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    placeholder="Ask us anything about rooms, amenities, pricing, or facilities..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-5 py-3 bg-dark-900 rounded-2xl text-sm text-white placeholder-white/25 ring-1 ring-white/[0.06] focus:ring-gold/60 focus:outline-none transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] resize-none font-body"
                  />
                  <p className="text-right text-xs text-white/30 mt-1 font-body">
                    {charCount}/500
                  </p>
                </div>

                {/* Honeypot — bots fill, humans don't see */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] h-0 w-0 opacity-0"
                />

                {errorMsg && (
                  <p className="text-red-400 text-sm font-body text-center">
                    {errorMsg}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-14 bg-gold text-black text-sm uppercase tracking-wider sm:tracking-widest font-semibold rounded-full hover:bg-gold-dark hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60 font-body"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-lg" />
                      Sending...
                    </span>
                  ) : (
                    "Send Enquiry"
                  )}
                </button>
              </form>
            )}
            </div>
          </motion.div>

          {/* RIGHT — WhatsApp Card */}
          <motion.div
            initial={{ opacity: 0, x: 60, filter: "blur(6px)" }}
            animate={isInView ? { opacity: 1, x: 0, filter: "blur(0px)" } : {}}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.32, 0.72, 0, 1] }}
            className="flex flex-col gap-8"
          >
            {/* Main WhatsApp CTA */}
            <div className="rounded-[1.75rem] bg-white/[0.03] p-1.5 ring-1 ring-whatsapp/10 flex-1"><div className="bg-dark-700 rounded-[calc(1.75rem-0.375rem)] p-8 md:p-10 shadow-[inset_0_1px_1px_rgba(255,255,255,0.06)] relative overflow-hidden flex flex-col justify-center h-full">
              {/* Green glow accent */}
              <div className="absolute -top-20 -right-20 w-60 h-60 bg-whatsapp/5 rounded-full blur-3xl pointer-events-none" />

              <div className="relative z-10">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-whatsapp/10 mb-8">
                  <i className="ri-whatsapp-line text-whatsapp text-4xl" />
                </div>

                <h3 className="text-white text-2xl md:text-3xl font-bold font-display mb-4">
                  Prefer to Chat?
                </h3>
                <p className="text-white/50 text-base font-body mb-8 leading-relaxed max-w-md">
                  Send us a message on WhatsApp for an instant reply. Ask about
                  rooms, pricing, availability, or anything else you&apos;d like
                  to know.
                </p>

                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full h-14 flex items-center justify-center gap-3 bg-whatsapp text-white text-sm uppercase tracking-wider sm:tracking-widest font-semibold rounded-full hover:brightness-110 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 cursor-pointer whitespace-nowrap font-body mb-8"
                >
                  <i className="ri-whatsapp-line text-xl" />
                  Chat on WhatsApp
                </a>

                {/* Call directly */}
                <div className="text-center mb-8">
                  <p className="text-white/40 text-sm font-body mb-2">
                    Or call us directly
                  </p>
                  <a
                    href="tel:+233256112666"
                    className="text-gold text-lg font-semibold font-body hover:text-gold-light transition-colors duration-300"
                  >
                    <i className="ri-phone-line mr-2" />
                    +233 25 611 2666
                  </a>
                </div>

                {/* Trust Indicators */}
                <div className="border-t border-white/10 pt-6">
                  <div className="flex items-center justify-center gap-6 flex-wrap">
                    <div className="flex items-center gap-2 text-white/50 text-sm font-body">
                      <i className="ri-chat-smile-2-line text-gold" />
                      <span>No commitment</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/50 text-sm font-body">
                      <i className="ri-flashlight-line text-gold" />
                      <span>Quick response</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/50 text-sm font-body">
                      <i className="ri-time-line text-gold" />
                      <span>24/7 support</span>
                    </div>
                  </div>
                </div>
              </div>
            </div></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
